import * as React from 'react'
import { graphql } from 'gatsby'

import Layout from '../../components/Layout/Layout'

import MainSection, { MainSectionParts } from '../../components/MainSection/MainSection'
import { Colors, ROUTE_PATH } from '../../utils/constants'
import Section from '../../components/Section/Section'
import { cls } from '../../utils/utils'
import SimpleMarkdown from '../../components/SimpleMarkdown/SimpleMarkdown'
import Image from '../../components/Image/Image'
import sectionStyles from '../../components/Section/Section.module.scss'
import Seo from '../../components/Seo/Seo'
import { IAboutUsProps, IAboutUsTemplateProps } from './AboutUs.types'
import styles from './AboutUs.module.scss'
import Testimonial from './components/Testimonial/Testimonial'

export const AboutUsTemplate: React.FC<IAboutUsTemplateProps> = ({
  title,
  background,
  paragraph,
  ourCustomers,
  customersFeedback,
  ourProducts,
  projectExamples,
  professionalAssociations,
}) => {
  const shiftFooter = () => {
    if (typeof window !== `undefined`) {
      document.querySelector('footer')?.classList.toggle(styles.footerShift)
      document.getElementsByClassName(sectionStyles.shiftParallelepiped)[0]?.classList.toggle(styles.parallelepipedShift)
    }
  }

  return (
    <>
      <MainSection>
        <MainSectionParts.FirstSection image={background} backgroundColor={Colors.MAIN}>
          <h1>{title}</h1>
        </MainSectionParts.FirstSection>

        <MainSectionParts.SecondSection
          hideBottomPadding
          smallPadding
          parallelepipedColor={'dark'}
          readyToTalkStyle={'dark'}
          readyToTalkText={'Get in Touch'}
        >
          <SimpleMarkdown className={styles.paragraph} body={paragraph} />
        </MainSectionParts.SecondSection>
      </MainSection>
      <Section backgroundColor={'dark'} parallelepipedColor={'grey'} readyToTalkStyle={'white'} readyToTalkText={'Get in Touch'}>
        <h2 className={styles.title}>{ourCustomers.title}</h2>
        <div className={styles.customersContainer}>
          {ourCustomers.customers.map((customer, key) => (
            <React.Fragment key={key}>
              {customer?.link ? (
                <a
                  href={customer.link}
                  key={key}
                  className={cls(styles.iconContainer, styles.iconContainerLink)}
                  rel="noreferrer noopener nofollow"
                  target="_blank"
                >
                  <Image image={customer.icon?.publicURL} className={styles.image} />
                </a>
              ) : (
                <div key={key} className={styles.iconContainer}>
                  <Image image={customer.icon?.publicURL} className={styles.image} />
                </div>
              )}
            </React.Fragment>
          ))}
        </div>
        <h2 className={styles.title}>{customersFeedback.title}</h2>
        <div className={styles.testimonials}>
          {customersFeedback.testimonials.map((testimonial, key) => (
            <Testimonial key={key} {...testimonial} quotationMarks={customersFeedback.quotationMarks} />
          ))}
        </div>
      </Section>
      <Section
        backgroundColor={'grey'}
        parallelepipedColor={'greyLighter'}
        readyToTalkStyle={'dark'}
        readyToTalkText={'Get in Touch'}
        shiftParallelepiped
        hideBottomPadding
      >
        <h2 className={styles.title}>{ourProducts.title}</h2>
        <div className={cls(styles.products)}>
          {ourProducts.products.map((product, key) => (
            <React.Fragment key={key}>
              {product?.link ? (
                <a
                  href={product?.link}
                  className={cls(styles.productsContainer, styles.productsContainerLink)}
                  onMouseEnter={shiftFooter}
                  onMouseLeave={shiftFooter}
                  rel="noreferrer noopener nofollow"
                  target="_blank"
                  key={key}
                >
                  <div className={styles.productsContainerText}>
                    <h2 className={styles.productsContainerTitle}>{product.title}</h2>
                    <h3 className={styles.productsContainerSummary}>{product.text}</h3>
                  </div>
                  <Image image={product.logo?.publicURL} className={cls(styles.image, styles.productsContainerImage)} />
                </a>
              ) : (
                <div className={cls(styles.productsContainer)} onMouseEnter={shiftFooter} onMouseLeave={shiftFooter} key={key}>
                  <div className={styles.productsContainerText}>
                    <h2 className={styles.productsContainerTitle}>{product.title}</h2>
                    <h3 className={styles.productsContainerSummary}>{product.text}</h3>
                  </div>
                  <Image image={product.logo?.publicURL} className={cls(styles.image, styles.productsContainerImage)} />
                </div>
              )}
            </React.Fragment>
          ))}
        </div>

        <h2 className={styles.title}>{projectExamples.title}</h2>
        <div className={styles.examplesContainer}>
          {projectExamples.projects.map((example, index) => (
            <div className={styles.examplesTile} key={index}>
              <h2 className={styles.examplesTitle}>{example.title}</h2>
              <h3 className={styles.examplesDescription}>{example.description}</h3>
              <div className={styles.examplesImageContainer}>
                {example.icons.map((image, key) => (
                  <Image image={image.image} className={styles.examplesImage} key={key} />
                ))}
              </div>
            </div>
          ))}
        </div>
        <h2 className={styles.title}>{professionalAssociations.title}</h2>
        <div className={styles.associations}>
          {professionalAssociations.associations.map((association, key) => (
            <a
              href={association.link}
              key={key}
              className={cls(styles.iconContainer, styles.iconContainerBig, styles.iconContainerLink)}
              rel="noreferrer noopener nofollow"
              target="_blank"
            >
              <SimpleMarkdown className={cls('text--normal', styles.associationTitle)} body={association.title} />
              <Image image={association.icon?.publicURL} className={styles.image} />
            </a>
          ))}
        </div>
      </Section>
    </>
  )
}

const AboutUs: React.FC<IAboutUsProps> = ({ data }) => (
  <Layout title={data.markdownRemark.frontmatter.title} backgroundColor={Colors.MAIN} headerLink={ROUTE_PATH.aboutUs.path}>
    <AboutUsTemplate {...data.markdownRemark.frontmatter} />
  </Layout>
)

export const pageQuery = graphql`
  query AboutUs($id: String!) {
    markdownRemark(id: { eq: $id }) {
      frontmatter {
        title
        background {
          publicURL
          childImageSharp {
            gatsbyImageData(layout: FULL_WIDTH)
          }
        }
        paragraph
        ourCustomers {
          title
          customers {
            icon {
              childImageSharp {
                gatsbyImageData(layout: FIXED)
              }
              publicURL
            }
            link
          }
        }
        customersFeedback {
          title
          quotationMarks {
            childImageSharp {
              gatsbyImageData(layout: FIXED)
            }
            publicURL
          }
          testimonials {
            clientDescription
            clientName
            clientTestimonial
            clientPhoto {
              childImageSharp {
                gatsbyImageData(layout: FIXED)
              }
              publicURL
            }
          }
        }
        ourProducts {
          title
          products {
            title
            text
            logo {
              childImageSharp {
                gatsbyImageData(layout: FIXED)
              }
              publicURL
            }
            link
          }
        }
        projectExamples {
          title
          projects {
            title
            description
            icons {
              image {
                publicURL
                childImageSharp {
                  gatsbyImageData(layout: CONSTRAINED)
                }
              }
            }
          }
        }
        professionalAssociations {
          title
          associations {
            title
            icon {
              childImageSharp {
                gatsbyImageData(layout: FIXED)
              }
              publicURL
            }
            link
          }
        }
      }
    }
  }
`

export default AboutUs

export const Head = ({ data }: IAboutUsProps) => {
  return <Seo title={data.markdownRemark.frontmatter.title} />
}
